<template>
  <CModal
    :show.sync="isVisible"
    :close-on-backdrop="false"
    size="lg"
    centered
    @update:show="handleClose"
  >
    <template #header>
      <h5 class="modal-title">
        <CIcon name="cil-warning" size="sm" class="mr-2"/>
        <translate translate-context="chargeback">Add Chargeback</translate>
      </h5>
      <button type="button" class="close" @click="close">
        <span>&times;</span>
      </button>
    </template>

    <div class="px-3 py-2">
      <!-- Step 1: Search by email -->
      <div class="mb-4">
        <label class="font-weight-bold">
          <translate translate-context="chargeback">Search by email</translate>
        </label>
        <CInput
          ref="emailInput"
          v-model="searchEmail"
          type="email"
          :placeholder="$pgettext('chargeback', 'Enter user email address...')"
          @input="handleSearchDebounced"
        />
        <div v-if="searching" class="text-muted small mt-1">
          <CSpinner size="sm" class="mr-1"/>
          <translate translate-context="chargeback">Searching...</translate>
        </div>
      </div>

      <!-- Step 2: Order results -->
      <div v-if="orderResults && !selectedOrder" class="mb-4">
        <label class="font-weight-bold d-flex justify-content-between">
          <span><translate translate-context="chargeback">Select an order</translate></span>
          <span class="text-muted font-weight-normal small">
            {{ orderResults.length }} <translate translate-context="chargeback">results</translate>
          </span>
        </label>

        <div v-if="orderResults.length === 0" class="alert alert-info">
          <translate translate-context="chargeback">No orders found for this email.</translate>
        </div>

        <div v-else class="order-list">
          <div
            v-for="order in orderResults"
            :key="order.id"
            class="order-item"
            :class="{ 'order-item--disabled': isFullyRefunded(order) }"
            @click="!isFullyRefunded(order) && selectOrder(order)"
          >
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <div class="font-weight-bold">
                  {{ order.order_number }}
                </div>
                <div class="small text-muted">
                  {{ order.created_at | formatDateTime }}
                </div>
                <div class="small" v-if="order.order_items && order.order_items.length">
                  {{ order.order_items.map(i => i.pica_service_name).join(', ') }}
                </div>
              </div>
              <div class="text-right">
                <div class="font-weight-bold">{{ order.price_grandtotal | currency(order.currency) }}</div>
                <div
                  v-if="hasPartialRefund(order)"
                  class="small text-warning"
                >
                  <translate translate-context="chargeback">Refundable</translate>: {{ order.total_remaining | currency(order.currency) }}
                </div>
                <CBadge v-if="isFullyRefunded(order)" color="secondary" class="mt-1">
                  <translate translate-context="chargeback">Fully refunded</translate>
                </CBadge>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 3: Selected order + amount + reason -->
      <div v-if="selectedOrder">
        <!-- Selected order summary -->
        <div class="alert alert-primary mb-3">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <div class="font-weight-bold">{{ selectedOrder.order_number }}</div>
              <div class="small mt-1">{{ selectedOrder.user_email }}</div>
              <div class="small text-muted">{{ selectedOrder.created_at | formatDateTime }}</div>
              <div class="small" v-if="selectedOrder.order_items && selectedOrder.order_items.length">
                {{ selectedOrder.order_items.map(i => i.pica_service_name).join(', ') }}
              </div>
            </div>
            <div class="text-right">
              <div class="font-weight-bold">{{ selectedOrder.price_grandtotal | currency(selectedOrder.currency) }}</div>
              <div
                v-if="hasPartialRefund(selectedOrder)"
                class="small text-warning"
              >
                <translate translate-context="chargeback">Refundable</translate>: {{ selectedOrder.total_remaining | currency(selectedOrder.currency) }}
              </div>
              <CButton
                color="light"
                size="sm"
                class="mt-2"
                @click="selectedOrder = null"
              >
                <translate translate-context="chargeback">Change</translate>
              </CButton>
            </div>
          </div>
        </div>

        <!-- Dispute fee -->
        <div class="mb-3">
          <label class="font-weight-bold">
            <translate translate-context="chargeback">Stripe dispute fee</translate>
          </label>
          <CInput
            v-model="disputeFee"
            type="number"
            step="0.01"
            min="0"
            :prepend="selectedOrder.currency"
            @input="recalculateTotal"
          />
        </div>

        <!-- Total amount -->
        <div class="mb-3">
          <label class="font-weight-bold">
            <translate translate-context="chargeback">Total amount</translate>
          </label>
          <CInput
            v-model="amount"
            type="number"
            step="0.01"
            min="0"
            :prepend="selectedOrder.currency"
          />
          <div class="small text-muted mt-1">
            <template v-if="hasPartialRefund(selectedOrder)">
              <translate translate-context="chargeback">Remaining</translate>: {{ selectedOrder.total_remaining | currency(selectedOrder.currency) }}
            </template>
            <template v-else>
              <translate translate-context="chargeback">Order total</translate>: {{ selectedOrder.price_grandtotal | currency(selectedOrder.currency) }}
            </template>
            + <translate translate-context="chargeback">fee</translate>: {{ disputeFee | currency(selectedOrder.currency) }}
            = <strong>{{ suggestedTotal | currency(selectedOrder.currency) }}</strong>
            <CLink
              v-if="amount != suggestedTotal"
              href="#"
              class="ml-2"
              @click.prevent="amount = suggestedTotal.toFixed(2)"
            >
              <translate translate-context="chargeback">Use suggested</translate>
            </CLink>
          </div>
        </div>

        <!-- Accrual date -->
        <div class="mb-3">
          <label class="font-weight-bold">
            <translate translate-context="chargeback">Accrual date</translate>
          </label>
          <CInput
            v-model="accrualDate"
            type="date"
          />
        </div>

        <!-- Reason -->
        <div class="mb-3">
          <label class="font-weight-bold">
            <translate translate-context="chargeback">Reason / Notes</translate>
          </label>
          <CTextarea
            v-model="reason"
            :placeholder="$pgettext('chargeback', 'Enter chargeback details...')"
            rows="3"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <CButton color="light" @click="close">
        <translate translate-context="chargeback">Cancel</translate>
      </CButton>
      <CButton
        color="danger"
        @click="handleSubmit"
        :disabled="!canSubmit || creating"
      >
        <CSpinner v-if="creating" size="sm" class="mr-1"/>
        <CIcon v-else name="cil-warning" size="sm" class="mr-1"/>
        <translate translate-context="chargeback">Create Chargeback</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import debounce from 'lodash/debounce'

const STRIPE_DISPUTE_FEE = 15

export default {
  name: 'AddChargebackModal',

  data() {
    return {
      isVisible: false,
      searchEmail: '',
      selectedOrder: null,
      amount: '',
      reason: '',
      disputeFee: STRIPE_DISPUTE_FEE,
      accrualDate: '',
    }
  },

  computed: {
    ...mapGetters('accounting/chargeback', ['searchResults', 'searching', 'creating']),

    orderResults() {
      if (!this.searchResults) return null
      return this.searchResults.results || []
    },

    suggestedTotal() {
      if (!this.selectedOrder) return 0
      const baseAmount = this.hasPartialRefund(this.selectedOrder)
        ? parseFloat(this.selectedOrder.total_remaining)
        : parseFloat(this.selectedOrder.price_grandtotal)
      return baseAmount + parseFloat(this.disputeFee || 0)
    },

    canSubmit() {
      return this.selectedOrder && this.amount > 0 && this.accrualDate
    },
  },

  methods: {
    ...mapActions('accounting/chargeback', ['searchOrders', 'createChargeback', 'clearSearchResults']),

    open() {
      this.isVisible = true
      this.resetForm()
      this.$nextTick(() => {
        this.$refs.emailInput?.$el?.querySelector('input')?.focus()
      })
    },

    close() {
      this.isVisible = false
      this.clearSearchResults()
    },

    handleClose(val) {
      if (!val) {
        this.close()
      }
    },

    resetForm() {
      this.searchEmail = ''
      this.selectedOrder = null
      this.amount = ''
      this.reason = ''
      this.disputeFee = STRIPE_DISPUTE_FEE
      this.accrualDate = ''
    },

    handleSearchDebounced: debounce(function() {
      this.searchOrders(this.searchEmail)
    }, 400),

    isFullyRefunded(order) {
      if (!order.order_items || order.order_items.length === 0) return false
      return order.order_items.every(item => item.fully_refunded)
    },

    hasPartialRefund(order) {
      if (!order || order.fully_refunded) return false
      return parseFloat(order.total_remaining) < parseFloat(order.price_grandtotal)
    },

    selectOrder(order) {
      this.selectedOrder = order
      this.disputeFee = STRIPE_DISPUTE_FEE
      this.$nextTick(() => {
        this.amount = this.suggestedTotal.toFixed(2)
      })
    },

    recalculateTotal() {
      if (!this.selectedOrder) return
      this.amount = this.suggestedTotal.toFixed(2)
    },

    async handleSubmit() {
      if (!this.canSubmit) return

      const payload = {
        order: this.selectedOrder.id,
        amount: parseFloat(this.amount) || 0,
        dispute_fee: parseFloat(this.disputeFee) || 0,
        pertinence_date: this.accrualDate || null,
      }
      if (this.reason.trim()) {
        payload.reason = this.reason.trim()
      }
      const success = await this.createChargeback(payload)

      if (success) {
        this.close()
        this.$emit('created')
      }
    },
  },
}
</script>

<style scoped>
.order-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #d8dbe0;
  border-radius: 0.25rem;
}

.order-item {
  padding: 12px 16px;
  border-bottom: 1px solid #d8dbe0;
  cursor: pointer;
  transition: background-color 0.15s;
}

.order-item:last-child {
  border-bottom: none;
}

.order-item:hover:not(.order-item--disabled) {
  background-color: #f0f4f7;
}

.order-item--disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #f8f9fa;
}
</style>
